import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Typography } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import UIUploadProgress from "../ui/UIUploadProgress";

const MAX_SIZE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/svg+xml"];

const LogoUpload = ({
  imagePreview,
  onFileChange,
  changeLabel,
  hintLabel,
  disabled,
  isUploading,
  uploadProgress,
  uploadProgressLabel,
  label,
  required,
  size,
  borderRadius,
}) => {
  const inputRef = useRef(null);
  const [fileError, setFileError] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Accepted formats: JPG, PNG, SVG");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setFileError("Max file size is 2MB");
      e.target.value = "";
      return;
    }

    setFileError(null);
    const reader = new FileReader();
    reader.onload = (ev) => onFileChange(file, ev.target.result);
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const busy = disabled || isUploading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0,
      }}
    >
      {label && (
        <Typography
          component="label"
          sx={{
            color: "var(--color-foreground-muted)",
            fontSize: "var(--text-label-md-font-size)",
            fontWeight: "var(--font-weight-500)",
            lineHeight: "var(--text-label-md-line-height)",
            letterSpacing: 0,
            marginBottom: "var(--space-8)",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "var(--color-error-400)" }}>
              {" "}
              *
            </Box>
          )}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          cursor: busy ? "not-allowed" : "pointer",
          pointerEvents: busy ? "none" : "auto",
          opacity: busy ? 0.85 : 1,
          width: size,
          height: size,
        }}
        onClick={() => {
          if (!busy) inputRef.current?.click();
        }}
      >
        <Avatar
          src={imagePreview || undefined}
          variant="rounded"
          sx={{
            width: size,
            height: size,
            bgcolor: "#f0f2f5",
            borderRadius,
          }}
        >
          {!imagePreview && (
            <BusinessIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
          )}
        </Avatar>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            top: 0,
            left: 0,
            right: 0,
            bgcolor: "rgb(15 23 42 / 80%)",
            p: 0,
            borderRadius,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <CameraAltOutlinedIcon sx={{ fontSize: 24, color: "#fff" }} />
            <Typography
              sx={{
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              {changeLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: "#6b7280", mt: 1, textAlign: "center", width: size }}
      >
        {hintLabel}
      </Typography>
      {fileError && (
        <Typography
          variant="caption"
          sx={{ color: "error.main", textAlign: "center" }}
        >
          {fileError}
        </Typography>
      )}
      {isUploading && (
        <UIUploadProgress
          preview={imagePreview}
          progress={uploadProgress}
          label={uploadProgressLabel}
        />
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.svg"
        style={{ display: "none" }}
        disabled={disabled}
        onChange={handleFileSelect}
      />
    </Box>
  );
};

LogoUpload.propTypes = {
  imagePreview: PropTypes.string,
  onFileChange: PropTypes.func.isRequired,
  changeLabel: PropTypes.string,
  hintLabel: PropTypes.string,
  disabled: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadProgressLabel: PropTypes.string,
  label: PropTypes.node,
  required: PropTypes.bool,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

LogoUpload.defaultProps = {
  imagePreview: null,
  disabled: false,
  isUploading: false,
  uploadProgress: undefined,
  uploadProgressLabel: undefined,
  label: null,
  required: false,
  size: 112,
  borderRadius: "8px",
};

export default LogoUpload;
